import { Card, Dropdown, Field, Toggle } from '@/design-system';
import {
    NHG_REGISTRATION_LABELS,
    NHG_REGISTRATIONS,
    type NhgRegistration,
    type TenantSettings,
    type TenantSettingsPatch,
} from '@/entities/tenant';

import { nhgCopy, settingsCopy } from '../copy';

/** Waar "Ingeschreven als" staat: Bedrijfsgegevens linkt hierheen. */
export const NHG_REGISTERED_AS_ID = 'nhg-ingeschreven-als';

export interface NhgSettingsCardProps {
    settings: TenantSettings;
    onChange: (patch: TenantSettingsPatch) => void;
    canManage: boolean;
    disabled?: boolean;
}

/**
 * De NHG-bijlage van het bedrijf (`includeNhgAppendix`, `nhgRegisteredAs` in
 * `/tenant/settings`).
 *
 * De schakelaar zet de bijlage aan of uit voor elk nieuw rapport van een
 * bouwkundige keuring; een vragenlijst die de bijlage zelf uitzet, blijft zonder.
 * "Ingeschreven als" is het vakje dat het NHG-model bij de keuringsinstantie
 * aankruist: alleen het bedrijf weet welk van de vier het is. Zie `docs/nhg.md`.
 */
export function NhgSettingsCard({
    settings,
    onChange,
    canManage,
    disabled = false,
}: NhgSettingsCardProps) {
    const locked = disabled || !canManage;
    const registeredAs = settings.nhgRegisteredAs ?? '';

    return (
        <Card padding="lg" as="section" aria-labelledby="nhg-settings-title">
            <h2
                id="nhg-settings-title"
                className="font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {nhgCopy.title}
            </h2>
            <p className="mt-1 mb-[6px] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {nhgCopy.intro}
            </p>

            <Toggle
                className="border-b border-[var(--border-subtle-2)]"
                label={nhgCopy.include}
                description={`${
                    settings.includeNhgAppendix ? nhgCopy.includeOn : nhgCopy.includeOff
                } ${nhgCopy.unchanged}`}
                checked={settings.includeNhgAppendix}
                disabled={locked}
                onCheckedChange={(checked) => onChange({ includeNhgAppendix: checked })}
            />

            <div className="pt-[14px]">
                <Field
                    id={NHG_REGISTERED_AS_ID}
                    label={nhgCopy.registeredAs}
                    description={
                        registeredAs === ''
                            ? nhgCopy.registeredAsEmpty
                            : nhgCopy.registeredAsDescription
                    }
                    size="md"
                >
                    <Dropdown<NhgRegistration | ''>
                        value={registeredAs}
                        disabled={locked}
                        options={[
                            { value: '', label: nhgCopy.registeredAsNone },
                            ...NHG_REGISTRATIONS.map((registration) => ({
                                value: registration,
                                label: NHG_REGISTRATION_LABELS[registration],
                            })),
                        ]}
                        onChange={(value) =>
                            onChange({ nhgRegisteredAs: value === '' ? null : value })
                        }
                    />
                </Field>
            </div>

            {canManage ? null : (
                <p className="mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {settingsCopy.readOnly}
                </p>
            )}
        </Card>
    );
}
